import { Form, Formik } from 'formik'
import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import * as Yup from 'yup'

import { addUser } from '../../utils/api'
import { useAppHistory } from '../../utils/useAppHistory'
import {
  Checkbox,
  Input,
  PasswordInput,
  Recaptcha,
  Textarea,
} from '../form/FormikWrappers'
import Button from '../ui/Button'
import FormButtons from '../ui/FormButtons'
import LabeledRow from '../ui/LabeledRow'
import { AuthPage } from '../ui/PageTemplate'
import AuthLinks from './AuthLinks'
import { ErrorMessage } from './AuthWrappers'
import { withAuthRedirect } from './withAuthRedirect'

const formToUser = (form) => ({ ...form, password_confirm: undefined })

const SignUpPage = () => {
  const history = useAppHistory()
  const recaptchaRef = useRef()
  const { t } = useTranslation()

  const handleSubmit = async (values) => {
    const newUser = formToUser(values)

    try {
      await addUser(newUser)
      history.push('/auth/confirmation/check-email')
    } catch (error) {
      toast.error(
        t('error_message.auth.signup_failed', {
          message: error.message || t('error_message.unknown_error'),
        }),
      )
      recaptchaRef.current.reset()
    }
  }

  return (
    <AuthPage>
      <h1>{t('glossary.sign_up')}</h1>
      <Formik
        initialValues={{
          email: '',
          password: '',
          password_confirm: '',
          name: '',
          bio: '',
          announcements_email: true,
        }}
        validationSchema={Yup.object({
          email: Yup.string().email().required(),
          password: Yup.string().min(6).required(),
          password_confirm: Yup.string()
            .oneOf([Yup.ref('password')])
            .required(),
          name: Yup.string(),
          bio: Yup.string(),
          'g-recaptcha-response': Yup.string().required(),
        })}
        onSubmit={handleSubmit}
      >
        {({ errors, dirty, isValid, isSubmitting, values }) => (
          <Form>
            <div>
              <Input
                type="email"
                name="email"
                label={t('glossary.email')}
                required
                autoComplete="off"
              />

              <PasswordInput
                name="password"
                label={t('glossary.password')}
                required
              />
              {errors.password && (
                <ErrorMessage>
                  {t(errors.password.key, errors.password.options)}
                </ErrorMessage>
              )}

              <PasswordInput
                name="password_confirm"
                label={t('users.password_confirmation')}
                required
                invalidWhenUntouched={values.password !== ''}
              />
              {errors.password_confirm && (
                <ErrorMessage>
                  {t(
                    errors.password_confirm.key,
                    errors.password_confirm.options,
                  )}
                </ErrorMessage>
              )}

              <Input
                type="text"
                name="name"
                label={t('glossary.name')}
                autoComplete="off"
              />

              <Textarea name="bio" label={t('users.bio')} />
            </div>
            <LabeledRow
              label={
                <label htmlFor="announcements_email">
                  {t('users.options.announcements_email')}
                </label>
              }
              left={<Checkbox id="announcements_email" />}
              style={{ margin: '16px 0' }}
            />

            <Recaptcha
              name="g-recaptcha-response"
              widthMargin={28}
              ref={(e) => {
                recaptchaRef.current = e
              }}
            />

            <FormButtons>
              <Button secondary type="reset">
                {t('form.button.reset')}
              </Button>
              <Button
                type="submit"
                disabled={!dirty || !isValid || isSubmitting}
              >
                {t('glossary.sign_up')}
              </Button>
            </FormButtons>
          </Form>
        )}
      </Formik>
      <AuthLinks exclude={['signUp']} />
    </AuthPage>
  )
}

export default withAuthRedirect(SignUpPage)
